import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getThemeTextColor, getThemeBackgroundColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import apiUtils from "../../utils/apiUtils";
import axios from "axios";
import { GlobalContext } from "../../contexts/GlobalContext";
import moment from "moment";
import HistoryIcon from "@mui/icons-material/History";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CompanyDocumentsTab from "./CompanyDocumentsTab";
import MSRDocumentsTab from "./MSRDocumentsTab";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

// Document types
const documentTypes = [
  { id: 1, name: "POA", code: "poa" },
  { id: 2, name: "Legal Notice", code: "legal_notice" },
  { id: 3, name: "Test Document", code: "test_doc" },
  { id: 4, name: "Dummy Doc 1", code: "dummy_doc_1" },
  { id: 5, name: "Dummy Doc 2", code: "dummy_doc_2" },
  { id: 6, name: "Dummy Doc 3", code: "dummy_doc_3" },
  { id: 7, name: "Dummy Doc 4", code: "dummy_doc_4" },
  { id: 8, name: "Dummy Doc 5", code: "dummy_doc_5" },
  { id: 9, name: "Dummy Doc 6", code: "dummy_doc_6" },
  { id: 10, name: "Dummy Doc 7", code: "dummy_doc_7" },
  { id: 11, name: "Dummy Doc 8", code: "dummy_doc_8" },
  { id: 12, name: "Dummy Doc 9", code: "dummy_doc_9" },
  { id: 13, name: "Dummy Doc 10", code: "dummy_doc_10" },
  { id: 14, name: "Dummy Doc 11", code: "dummy_doc_11" },
  { id: 15, name: "Dummy Doc 12", code: "dummy_doc_12" },
  { id: 16, name: "Dummy Doc 13", code: "dummy_doc_13" },
  { id: 17, name: "Dummy Doc 14", code: "dummy_doc_14" },
  { id: 18, name: "Dummy Doc 15", code: "dummy_doc_15" },
];

// Dummy data for existing documents
const existingDocuments = [
  { countryCode: "NL", documentCode: "poa" },
  { countryCode: "NL", documentCode: "legal_notice" },
  { countryCode: "NL", documentCode: "test_doc" },
  { countryCode: "BE", documentCode: "poa" },
  { countryCode: "BE", documentCode: "legal_notice" },
  { countryCode: "FR", documentCode: "poa" },
  { countryCode: "FR", documentCode: "test_doc" },
  { countryCode: "DE", documentCode: "poa" },
  { countryCode: "DE", documentCode: "legal_notice" },
  { countryCode: "DE", documentCode: "test_doc" },
  { countryCode: "ES", documentCode: "legal_notice" },
  { countryCode: "IT", documentCode: "poa" },
  { countryCode: "IT", documentCode: "test_doc" },
];

const documentTypeMap = { poa: 2, legal_notice: 3, test_doc: 4 };

const CustomerDocuments = ({ customerId }) => {
  const theme = useTheme();
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: "", subTitle: "" });
  const [uploadDialog, setUploadDialog] = useState({ isOpen: false, countryCode: "", documentCode: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const { globalData } = useContext(GlobalContext);
  const uploadedBy = globalData?.userId;
  const [historyDialog, setHistoryDialog] = useState({ open: false, docs: [], docName: "", countryName: "" });
  const [historyFilenames, setHistoryFilenames] = useState({});
  const scrollBoxRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  // Get countries from API
  const countryData = apiUtils.useApiData("/api/v1/countries");

  // Fetch customer documents from API
  useEffect(() => {
    if (!customerId) return;
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
        setCustomerDocuments(response.data || []);
      } catch (error) {
        setCustomerDocuments([]);
      }
    };
    fetchDocuments();
  }, [customerId]);

  // Fetch filenames when opening history dialog
  useEffect(() => {
    const fetchFilenames = async () => {
      if (!historyDialog.open || !historyDialog.docs.length) return;
      const newFilenames = {};
      await Promise.all(
        historyDialog.docs.map(async (doc) => {
          try {
            const res = await axios.get(`/api/v1/file/${doc.fileId}`);
            newFilenames[doc.fileId] = res.data?.fileName || "";
          } catch {
            newFilenames[doc.fileId] = "";
          }
        })
      );
      setHistoryFilenames(newFilenames);
    };
    fetchFilenames();
  }, [historyDialog]);

  const handleUpload = (countryCode, documentCode) => {
    setUploadDialog({ isOpen: true, countryCode, documentCode });
  };

  const handleDownload = (countryCode, documentCode) => {
    // TODO: Implement file download
    setNotify({
      isOpen: true,
      message: `Downloading document for ${countryCode} - ${documentCode}`,
      type: "info",
    });
  };

  const isDocumentExists = (countryId, documentType) => {
    // Debugging output
    console.log("customerDocuments", customerDocuments);
    console.log("Checking:", { countryId, documentType, customerId });
    return customerDocuments.some(
      (doc) =>
        Number(doc.entityId) === Number(customerId) &&
        Number(doc.documentType) === Number(documentType) &&
        Number(doc.countryId) === Number(countryId) &&
        Number(doc.deleted) === 0
    );
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCellDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "action.hover";
  };

  const handleCellDragLeave = (event) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "";
  };

  const handleCellDrop = (event, countryCode, documentCode) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "";
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadDialog({ isOpen: true, countryCode, documentCode });
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile || !customerId || !uploadedBy) return;
    setNotify({ isOpen: true, message: "Uploading...", type: "info" });
    console.log(selectedFile);
    try {
      // 1. POST file info
      const fileInfo = {
        documentType: documentTypeMap[uploadDialog.documentCode],
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: uploadedBy,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
      };
      const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
      const fileId = fileInfoRes.data.fileId;

      // 2. POST file to Azure
      const formData = new FormData();
      formData.append("file", selectedFile, fileId);
      await axios.post("/api/v1/azureblobstorage", formData);

      // 3. Determine next document version
      const country = countryData?.find((c) => c.countryCode === uploadDialog.countryCode);
      const countryId = country ? country.countryId : null;
      if (!countryId) {
        setNotify({ isOpen: true, message: "Country not found.", type: "error" });
        return;
      }
      const docType = documentTypeMap[uploadDialog.documentCode];
      const docs = customerDocuments.filter(
        (d) => Number(d.countryId) === Number(countryId) && Number(d.documentType) === Number(docType) && Number(d.deleted) === 0
      );
      const latestVersion = docs.length > 0 ? Math.max(...docs.map((d) => d.documentVersion)) : 0;
      const nextVersion = latestVersion + 1;

      // 4. POST document info
      const documentInfo = {
        entityId: customerId,
        documentType: docType,
        documentVersion: nextVersion,
        uploadedBy: uploadedBy,
        uploadDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        fileId: fileId,
        deleted: 0,
        countryId: countryId,
      };
      await axios.post("/api/v1/customerdocuments", documentInfo);

      setNotify({ isOpen: true, message: "Document uploaded successfully!", type: "success" });
      setUploadDialog({ isOpen: false, countryCode: "", documentCode: "" });
      setSelectedFile(null);
      // Refresh documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      setCustomerDocuments(response.data || []);
    } catch (error) {
      setNotify({ isOpen: true, message: "Error uploading document.", type: "error" });
    }
  };

  const handleUploadCancel = () => {
    setUploadDialog({ isOpen: false, countryCode: "", documentCode: "" });
    setSelectedFile(null);
  };

  const getDocumentName = (documentCode) => {
    const doc = documentTypes.find((d) => d.code === documentCode);
    return doc ? doc.name : documentCode;
  };

  const getCountryName = (countryCode) => {
    const country = countryData?.find((c) => c.countryCode === countryCode);
    return country ? country.countryName : countryCode;
  };

  // Helper to check VAT and Excise requirements
  const hasVatDocs = (countryId) =>
    isDocumentExists(countryId, documentTypeMap["poa"]) && isDocumentExists(countryId, documentTypeMap["legal_notice"]);

  const hasExciseDocs = (countryId) =>
    isDocumentExists(countryId, documentTypeMap["poa"]) && isDocumentExists(countryId, documentTypeMap["test_doc"]);

  const downloadFile = async (fileId) => {
    try {
      // 1. Fetch file info to get the filename
      const fileInfoRes = await axios.get(`/api/v1/file/${fileId}`);
      const fileName = fileInfoRes.data?.fileName || "document.pdf";
      // 2. Fetch the file blob
      const response = await axios({
        url: "/api/v1/azureblobstorage/" + fileId,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (error) {
      setNotify({ isOpen: true, message: "Error downloading file.", type: "error" });
      console.error("Error downloading file:", error);
    }
  };

  const handleWheel = (e) => {
    // Get the bounding rect of the scrollable box
    const box = scrollBoxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    // The sticky columns are 180+60+70 = 310px wide
    const stickyWidth = 310;
    // If mouse is over the document type columns area
    if (e.clientX - rect.left > stickyWidth) {
      // Only scroll horizontally
      e.preventDefault();
      box.scrollLeft += e.deltaY;
    }
  };

  const columns = [
    {
      field: "countryName",
      headerName: "Country",
      width: 150,
      headerAlign: "center",
      renderCell: (params) => <Box sx={{ background: "background.paper" }}>{params.value}</Box>,
      pinToLeft: true,
    },
    {
      field: "vat",
      headerName: "VAT",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            background: hasVatDocs(params.row.countryId) ? "success.light" : "action.hover",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {hasVatDocs(params.row.countryId) ? (
            <Checkbox checked color="success" inputProps={{ "aria-label": "VAT docs present" }} />
          ) : null}
        </Box>
      ),
      pinToLeft: true,
    },
    {
      field: "excise",
      headerName: "Excise",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            background: hasExciseDocs(params.row.countryId) ? "success.light" : "action.hover",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {hasExciseDocs(params.row.countryId) ? (
            <Checkbox checked color="success" inputProps={{ "aria-label": "Excise docs present" }} />
          ) : null}
        </Box>
      ),
      pinToLeft: true,
    },
    ...documentTypes.map((doc) => ({
      field: doc.code,
      headerName: doc.name,
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const docs = customerDocuments.filter(
          (d) =>
            Number(d.countryId) === Number(params.row.countryId) &&
            Number(d.documentType) === Number(documentTypeMap[doc.code]) &&
            Number(d.deleted) === 0
        );
        const latestDoc = docs.length > 0 ? docs.reduce((a, b) => (a.documentVersion > b.documentVersion ? a : b)) : null;
        return (
          <Box
            sx={{
              cursor: "pointer",
              "&:hover": { backgroundColor: "action.hover" },
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
            onDragOver={handleCellDragOver}
            onDragLeave={handleCellDragLeave}
            onDrop={(e) => handleCellDrop(e, params.row.countryCode, doc.code)}
            onClick={(e) => {
              // Only trigger if the click wasn't on an icon button
              if (!e.target.closest(".MuiIconButton-root")) {
                handleUpload(params.row.countryCode, doc.code);
              }
            }}
          >
            {latestDoc && (
              <IconButton
                color="success"
                onClick={() => downloadFile(latestDoc.fileId)}
                aria-label={`Download ${doc.name} for ${params.row.countryName}`}
                size="small"
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            )}
            {docs.length > 1 && (
              <IconButton
                color="info"
                onClick={() => setHistoryDialog({ open: true, docs, docName: doc.name, countryName: params.row.countryName })}
                aria-label={`Show history for ${doc.name} in ${params.row.countryName}`}
                size="small"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              color="warning"
              onClick={() => handleUpload(params.row.countryCode, doc.code)}
              aria-label={`Upload ${doc.name} for ${params.row.countryName}`}
              size="small"
            >
              <CloudUploadIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    })),
  ];

  const rows =
    countryData?.map((country) => ({
      id: country.countryId,
      countryId: country.countryId,
      countryCode: country.countryCode,
      countryName: country.countryName,
    })) || [];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <React.Fragment>
      <Helmet title="Customer Documents" />
      <Divider my={6} />
      <Box>
        <Card mb={0}>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="document tabs"
            sx={{
              mb: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
              minHeight: 44,
              "& .MuiTab-root": {
                minHeight: 44,
                fontWeight: 500,
                textTransform: "none",
                ...getThemeTextColor(theme),
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 2,
                backgroundColor: "#ff9800", // Orange indicator
              },
              "& .Mui-selected": {
                color: "#ff9800 !important", // Orange selected tab text
              },
            }}
          >
            <Tab label="Company Documents" />
            <Tab label="MSR Documents" />
          </Tabs>
          {activeTab === 0 && <CompanyDocumentsTab customerId={customerId} />}
          {activeTab === 1 && <MSRDocumentsTab customerId={customerId} />}
        </Card>
      </Box>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog.isOpen} onClose={handleUploadCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {getDocumentName(uploadDialog.documentCode)} for {getCountryName(uploadDialog.countryCode)}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "grey.300",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              bgcolor: isDragging ? "action.hover" : "background.paper",
              cursor: "pointer",
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
            {selectedFile ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Selected File:
                </Typography>
                <Typography variant="body1" color="primary">
                  {selectedFile.name}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Drag and drop a file here, or click to select a file
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Supported formats: PDF, DOC, DOCX
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCancel}>Cancel</Button>
          <Button onClick={handleUploadConfirm} variant="contained" color="primary" disabled={!selectedFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ ...historyDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          History for {historyDialog.docName} in {historyDialog.countryName}
        </DialogTitle>
        <DialogContent>
          {historyDialog.docs.length === 0 ? (
            <Typography>No history available.</Typography>
          ) : (
            <>
              {historyDialog.docs
                .sort((a, b) => b.documentVersion - a.documentVersion)
                .map((doc) => (
                  <Box key={doc.customerDocumentId} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography>
                      Version {doc.documentVersion} - {moment(doc.uploadDate).format("YYYY-MM-DD HH:mm")} -{" "}
                      {historyFilenames[doc.fileId] || ""}
                    </Typography>
                    <IconButton
                      color="success"
                      onClick={() => downloadFile(doc.fileId)}
                      aria-label={`Download version ${doc.documentVersion}`}
                      size="small"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ ...historyDialog, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </React.Fragment>
  );
};

export default CustomerDocuments;
